import type { Locale } from '@/i18n/locales'
import { routing } from '@/i18n/routing'

/*
 * The travel page's `/{locale}/travel/{fromRegion}/{fromCity}/{toRegion}/{toCity}/`
 * namespace, parsed by structure alone: the locale is a group this app declares, and the
 * four segments beneath are carried as written
 * (→ invariants.md#url-validity-is-the-stores-answer). Which pairs are pages is the
 * manifest's answer, and who answers a request the request boundary's.
 */

export const TRAVEL_SEGMENT = 'travel'
const PAIR_SEGMENTS = 4

export interface TravelPath {
    readonly locale: Locale
    /** `/travel/{fromRegion}/{fromCity}/{toRegion}/{toCity}`, segments as requested. */
    readonly slug: string
}

/** A segment decoded once, as the page reads it; one that is not valid percent-encoding
 *  is kept as written, and so names no pair. */
export function decodeOnce(segment: string): string {
    try {
        return decodeURIComponent(segment)
    } catch {
        return segment
    }
}

/** `/en-us/travel/us-ma/boston/us-ny/new-york/` → its locale and slug, or `undefined` for
 *  a path outside the namespace. */
export function travelPath(pathname: string): TravelPath | undefined {
    const [empty, locale = '', first = '', ...segments] = pathname.replace(/\/$/, '').split('/')
    if (empty !== '' || first !== TRAVEL_SEGMENT || segments.length !== PAIR_SEGMENTS) {
        return undefined
    }
    if (segments.includes('') || !(routing.locales as readonly string[]).includes(locale)) {
        return undefined
    }
    return { locale: locale as Locale, slug: `/${TRAVEL_SEGMENT}/${segments.join('/')}` }
}

/** The slug the page type serves for four route segments, each decoded once. */
export const travelSlug = (segments: readonly [string, string, string, string]): string =>
    `/${TRAVEL_SEGMENT}/${segments.map(decodeOnce).join('/')}`
